import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Loader2, RefreshCw, AlertTriangle, CheckCircle2, Clock, PauseCircle, Check, ArrowUpRight, ArrowDownRight, Minus,
  X, ExternalLink, ChevronRight, Package, ListChecks,
} from 'lucide-react';
import api from '../api/client';

// Team progress — product work for management, read live from Jira (project
// SCRUM) by GET /internal/team-progress. Separate from MIS on purpose: MIS is
// "how is the business doing", this is "how is the product work going". The
// backend does the summarising (app/services/team_progress.py, including the
// short area names); this page only lays it out, most important first: this
// sprint's ring, what needs attention, each area, then what was delivered.
// Every card opens the work behind it in a pop-up on this page — management
// usually has no Jira login, so nothing here depends on opening Jira.

// Jira's epic colour names → hex, so each area's ring matches its colour in Jira.
const EPIC_COLORS = {
  purple: '#6554C0', dark_purple: '#403294', blue: '#2684FF', dark_blue: '#0747A6',
  green: '#36B37E', dark_green: '#006644', teal: '#00B8D9', dark_teal: '#008DA6',
  yellow: '#FFC400', dark_yellow: '#FF8B00', orange: '#FF991F', dark_orange: '#B65C02',
  grey: '#97A0AF', dark_grey: '#505F79',
};

// Jira's own status names, so the page reads the same as the board.
const STATE_LABEL = { todo: 'To Do', doing: 'In Progress', testing: 'In Review', done: 'Done' };
const OPEN_ORDER = { testing: 0, doing: 1, todo: 2 };
const POPUP_LIMIT = 5;

const needsAttention = (i) => i.state !== 'done' && (i.late || i.stalled_days != null);

// Unfinished work: problems first, then closest to finished, then soonest due.
function sortOpen(list) {
  return [...list].sort((a, b) => {
    const rank = (i) => (i.late ? 0 : i.stalled_days != null ? 1 : 2);
    if (rank(a) !== rank(b)) return rank(a) - rank(b);
    if (OPEN_ORDER[a.state] !== OPEN_ORDER[b.state]) return OPEN_ORDER[a.state] - OPEN_ORDER[b.state];
    if ((a.due || '9') !== (b.due || '9')) return (a.due || '9').localeCompare(b.due || '9');
    return (a.area || '').localeCompare(b.area || '');
  });
}

// Finished work: newest first.
const sortDone = (list) => [...list].sort((a, b) => (b.done_on || '').localeCompare(a.done_on || ''));

function shortDate(iso) {
  if (!iso) return null;
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toLocaleDateString('en-CA');
}

function Ring({ pct, size, stroke, color, label }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const filled = Math.max(0, Math.min(100, pct || 0));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${filled}% done`} className="tp-ring">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg-subtle)" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={`${(c * filled) / 100} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="tp-ring__label" style={{ fontSize: label }}>
        {filled}%
      </text>
    </svg>
  );
}

function Pill({ tone, children }) {
  return <span className={`tp-pill tp-pill--${tone}`}>{children}</span>;
}

// One status per area card, most urgent first.
function areaStatus(a) {
  if (a.late > 0) return { tone: 'bad', text: `${plural(a.late, 'item', 'items')} late` };
  if (a.health === 'red') return { tone: 'bad', text: `Late, due ${shortDate(a.target)}` };
  if (a.stalled > 0) return { tone: 'warn', text: 'Stalled' };
  if (a.health === 'yellow') return { tone: 'warn', text: `Due ${shortDate(a.target)}` };
  if (a.target) return { tone: 'good', text: `On track, ${shortDate(a.target)}` };
  return { tone: 'none', text: 'No date yet' };
}

function WeekCompare({ now, before }) {
  if (before == null) return null;
  const Icon = now > before ? ArrowUpRight : now < before ? ArrowDownRight : Minus;
  const tone = now > before ? 'good' : now < before ? 'bad' : 'none';
  return (
    <span className={`tp-compare tp-compare--${tone}`}>
      <Icon size={14} /> vs {before} last week
    </span>
  );
}

// A card that opens its details. A real <button>, so it works from the keyboard.
function ClickCard({ className = '', onClick, label, children }) {
  return (
    <button type="button" className={`tp-click ${className}`} onClick={onClick} aria-label={label}>
      {children}
    </button>
  );
}

// A Jira status as a coloured badge, in the same colours as the bars.
function StatusBadge({ state, count }) {
  return <span className={`tp-status tp-status--${state}`}>{count != null ? `${count} ` : ''}{STATE_LABEL[state]}</span>;
}

// The date note beside a line's badge: late / stalled / due / done on. None
// when there is nothing to add to the status.
function lineNote(item) {
  if (item.state === 'done') return { text: item.done_on ? shortDate(item.done_on) : null, tone: 'none' };
  if (item.late) return { text: `Late since ${shortDate(item.due)}`, tone: 'bad' };
  if (item.stalled_days != null) return { text: `Stalled ${item.stalled_days} days`, tone: 'warn' };
  if (item.due) return { text: `Due ${shortDate(item.due)}`, tone: 'none' };
  return { text: null, tone: 'none' };
}

// One piece of work as one short line; clicking it shows who, area, type and
// the ticket (a Jira link, which only helps people who have a Jira login).
function Line({ item, jiraUrl, startOpen = false }) {
  const [open, setOpen] = useState(startOpen);
  const note = lineNote(item);
  return (
    <li className={`tp-line tp-line--${note.tone}${open ? ' tp-line--open' : ''}`}>
      <button type="button" className="tp-line__row" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="tp-line__title">{item.summary}</span>
        <span className="tp-line__side">
          {note.text && <span className="tp-line__when">{note.text}</span>}
          <StatusBadge state={item.state} />
        </span>
      </button>
      {open && (
        <div className="tp-line__detail">
          <span className="tp-line__facts">
            <span>{item.owner || 'Nobody assigned'}</span>
            {item.area && <span>{item.area}</span>}
            <span className={`tp-kind tp-kind--${item.kind}`}>{item.kind === 'fix' ? 'Bug fix' : 'Feature'}</span>
            {jiraUrl ? (
              <a className="tp-jira" href={`${jiraUrl}/browse/${item.key}`} target="_blank" rel="noopener noreferrer" title="Opens this ticket in Jira in a new tab (needs a Jira login)">
                {item.key} <ExternalLink size={12} />
              </a>
            ) : (
              <span>{item.key}</span>
            )}
          </span>
        </div>
      )}
    </li>
  );
}

// The first few lines, then "Show N more".
function ShortList({ items, jiraUrl }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, POPUP_LIMIT);
  return (
    <>
      <ul className="tp-lines">{shown.map((it) => <Line key={it.key} item={it} jiraUrl={jiraUrl} />)}</ul>
      {items.length > POPUP_LIMIT && (
        <button type="button" className="tp-link tp-showmore" onClick={() => setAll((v) => !v)}>
          {all ? 'Show fewer' : `Show ${items.length - POPUP_LIMIT} more`}
        </button>
      )}
    </>
  );
}

// The Jira statuses in Jira's own order, as one bar with the counts under it;
// `extra` sits at the right end of the counts line.
function ProgressBar({ items, extra }) {
  if (items.length === 0) return null;
  const count = (state) => items.filter((i) => i.state === state).length;
  const parts = [
    { n: count('todo'), cls: 'todo', label: 'To Do' },
    { n: count('doing'), cls: 'doing', label: 'In Progress' },
    { n: count('testing'), cls: 'testing', label: 'In Review' },
    { n: count('done'), cls: 'done', label: 'Done' },
  ].filter((p) => p.n > 0);
  return (
    <div className="tp-bar2">
      <div className="tp-bar2__track" role="img" aria-label={parts.map((p) => `${p.n} ${p.label}`).join(', ')}>
        {parts.map((p) => <span key={p.cls} className={`tp-bar2__seg tp-bar2__seg--${p.cls}`} style={{ flex: p.n }} />)}
      </div>
      <div className="tp-bar2__legend">
        {parts.map((p) => <span key={p.cls}><i className={`tp-dot tp-dot--${p.cls}`} />{p.n} {p.label}</span>)}
        {extra && <span className="tp-bar2__extra">{extra}</span>}
      </div>
    </div>
  );
}

function TabBody({ tab, jiraUrl }) {
  if (tab.items.length === 0) {
    return (
      <div className="tp-calm tp-calm--plain">
        <CheckCircle2 size={18} /> {tab.kind === 'open' ? 'Nothing left to do here.' : 'Nothing here yet.'}
      </div>
    );
  }
  if (tab.kind === 'done') return <ShortList items={sortDone(tab.items)} jiraUrl={jiraUrl} />;
  const problems = sortOpen(tab.items.filter(needsAttention));
  const rest = sortOpen(tab.items.filter((i) => !needsAttention(i)));
  return (
    <>
      {problems.length > 0 && (
        <section className="tp-popsec">
          <h3 className="tp-popsec__title tp-popsec__title--bad"><AlertTriangle size={14} /> Needs attention · {problems.length}</h3>
          <ShortList items={problems} jiraUrl={jiraUrl} />
        </section>
      )}
      {rest.length > 0 && (
        <section className="tp-popsec">
          <h3 className="tp-popsec__title">{problems.length > 0 ? 'Still to do' : 'Left to do'} · {rest.length}</h3>
          <ShortList items={rest} jiraUrl={jiraUrl} />
        </section>
      )}
    </>
  );
}

// The one pop-up every card opens, built for a quick read: a bar of how far
// along it is, problems first, short lines, finished work behind its own tab.
// detail = { title, subtitle, bar?: items[], tabs: [{ label, kind: 'open'|'done', items }], single?: item }
function DetailModal({ detail, jiraUrl, onClose }) {
  // Open on the first tab that has something in it (a finished area opens on "Done").
  const [tabIndex, setTabIndex] = useState(() => Math.max(0, (detail.tabs || []).findIndex((t) => t.items.length > 0)));
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tabs = detail.tabs || [];
  const tab = tabs[tabIndex];
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal tp-modal" role="dialog" aria-modal="true" aria-labelledby="tp-modal-title" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <div className="tp-modal__head">
            <h2 className="modal__title" id="tp-modal-title">{detail.title}</h2>
            {detail.subtitle && <p className="tp-modal__sub">{detail.subtitle}</p>}
          </div>
          <button className="modal__close" onClick={onClose} aria-label="Close"><X size={20} /></button>
        </div>
        <div className="modal__body tp-modal__body">
          {detail.bar && <ProgressBar items={detail.bar} />}
          {detail.single ? (
            <ul className="tp-lines"><Line item={detail.single} jiraUrl={jiraUrl} startOpen /></ul>
          ) : (
            <>
              {tabs.length > 1 && (
                <div className="tp-tabs" role="tablist">
                  {tabs.map((t, i) => (
                    <button
                      key={t.label} type="button" role="tab" aria-selected={i === tabIndex}
                      className={`tp-tab${i === tabIndex ? ' tp-tab--on' : ''}`} onClick={() => setTabIndex(i)}
                    >
                      {t.label} · {t.items.length}
                    </button>
                  ))}
                </div>
              )}
              {tab && <TabBody key={tab.label} tab={tab} jiraUrl={jiraUrl} />}
              <p className="tp-modal__hint">Click a line to see who is on it, the area and the ticket.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// "Left to do" first, "Done" second, with the bar over both.
const splitTabs = (list) => ({
  bar: list,
  tabs: [
    { label: 'Left to do', kind: 'open', items: list.filter((i) => i.state !== 'done') },
    { label: 'Done', kind: 'done', items: list.filter((i) => i.state === 'done') },
  ],
});

const sprintTitle = (sp) => (sp.version ? `${sp.label} · ${sp.version}` : sp.label);

// "Ready" = built: In Review (on preprod, waiting for the next release) plus Done.
const readyPct = (list) => (list.length ? Math.round((100 * list.filter((i) => i.state === 'testing' || i.state === 'done').length) / list.length) : 0);

// One sprint as one bar of Jira statuses, with how much is ready and how much
// is waiting for the next release (= In Review). Done only means "in prod", and
// work reaches prod in one release, so a done-only ring sat near 0% all sprint.
function SprintCard({ title, sub, list, onClick, label }) {
  const waiting = list.filter((i) => i.state === 'testing').length;
  return (
    <ClickCard className="tp-sprint" onClick={onClick} label={label}>
      <span className="tp-sprint__head">
        <span className="tp-sprint__title">{title}{sub && <span className="tp-sprint__sub"> · {sub}</span>}</span>
        <span className="tp-sprint__pct">{readyPct(list)}% <small>ready</small></span>
      </span>
      <ProgressBar
        items={list}
        extra={waiting > 0
          ? <span className="tp-sprint__waiting">{waiting} waiting for release</span>
          : <span>Nothing waiting for release</span>}
      />
    </ClickCard>
  );
}

// Jira's statuses in Jira's order, named exactly as Jira names them.
const PERSON_PARTS = [
  { cls: 'todo', label: 'To Do' },
  { cls: 'doing', label: 'In Progress' },
  { cls: 'testing', label: 'In Review' },
  { cls: 'done', label: 'Done' },
];

// Who has what: counts only, no percentages. While a sprint runs, each person's
// tickets in the current sprints, Done included (one release's worth, so no
// all-time pile-up); with no sprint running, their open work. "No owner" first
// (it is the row someone has to act on), then the most work first, ties A–Z.
function groupByPerson(items, withDone) {
  const people = new Map();
  for (const it of items) {
    if (it.state === 'done' && !withDone) continue;
    const name = it.owner || null;
    if (!people.has(name)) people.set(name, { name, counts: { todo: 0, doing: 0, testing: 0, done: 0 } });
    people.get(name).counts[it.state] += 1;
  }
  return [...people.values()]
    .map((p) => ({ ...p, total: Object.values(p.counts).reduce((a, b) => a + b, 0), open: p.counts.todo + p.counts.doing + p.counts.testing }))
    .filter((p) => (p.name === null ? p.open > 0 : p.total > 0))
    .sort((a, b) => (b.name === null) - (a.name === null) || b.total - a.total || (a.name || '').localeCompare(b.name || ''));
}

const initials = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

// The bar's length is the person's count against the busiest row, so
// 2 tickets and 6 tickets don't look the same.
function PersonRow({ person, maxTotal, onClick }) {
  const parts = PERSON_PARTS.filter((p) => person.counts[p.cls] > 0);
  const nobody = person.name === null;
  return (
    <li>
      <button
        type="button" className={`tp-person${nobody ? ' tp-person--nobody' : ''}`} onClick={onClick}
        aria-label={`Show ${nobody ? 'unassigned work' : `${person.name}'s work`}`}
      >
        <span className="tp-person__avatar">{nobody ? <AlertTriangle size={14} /> : initials(person.name)}</span>
        <span className="tp-person__name">{nobody ? 'No owner' : person.name}</span>
        <span className="tp-person__bar" aria-hidden="true" style={{ width: `${Math.max(8, Math.round((100 * person.total) / maxTotal))}%` }}>
          {parts.map((p) => <span key={p.cls} className={`tp-bar2__seg--${p.cls}`} style={{ flex: person.counts[p.cls] }} />)}
        </span>
        <span className="tp-person__text">
          {nobody
            ? `${plural(person.open, 'ticket needs', 'tickets need')} someone assigned`
            : parts.map((p) => <StatusBadge key={p.cls} state={p.cls} count={person.counts[p.cls]} />)}
        </span>
        <ChevronRight size={16} className="tp-person__go" />
      </button>
    </li>
  );
}

const COMING_LIMIT = 5;
const byDue = (a, b) => (a.due || '9').localeCompare(b.due || '9') || (a.area || '').localeCompare(b.area || '');

// One group of "Coming next": one line per ticket (long titles cut, the full
// title is in the click-through), the area, a "Fix" tag on bugs (bugs are named
// after the problem), the status where the group mixes them, and the due date.
function ComingGroup({ title, release, list, showStatus, onItem, onAll }) {
  return (
    <div className="tp-cgroup">
      <p className={`tp-cgroup__title${release ? ' tp-cgroup__title--release' : ''}`}>
        {release ? <Package size={14} /> : <ListChecks size={14} />} {title}
      </p>
      <ul className="tp-clines">
        {list.slice(0, COMING_LIMIT).map((it) => (
          <li key={it.key}>
            <button type="button" className="tp-cline" onClick={() => onItem(it.key)}>
              {it.kind === 'fix' && <span className="tp-fix">Fix</span>}
              <span className="tp-cline__title">{it.summary}</span>
              {it.area && <span className="tp-cline__area">{it.area}</span>}
              {showStatus && <StatusBadge state={it.state} />}
              <span className={`tp-cline__due${it.late ? ' tp-cline__due--late' : ''}`}>{it.due ? `due ${shortDate(it.due)}` : ''}</span>
            </button>
          </li>
        ))}
      </ul>
      {list.length > COMING_LIMIT && (
        <button type="button" className="tp-link tp-more" onClick={onAll}>See all {list.length} <ChevronRight size={14} /></button>
      )}
    </div>
  );
}

// Reference lists folded to one line ("13 areas delivered") that open on click,
// so they don't take up the bottom of the page.
function FoldTiles({ icon, label, tone, areas, onArea }) {
  const Icon = icon;
  const [open, setOpen] = useState(false);
  return (
    <div className="tp-fold">
      <button type="button" className={`tp-fold__toggle tp-fold__toggle--${tone}`} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <Icon size={15} /> {label} <ChevronRight size={14} className={`tp-fold__chev${open ? ' tp-fold__chev--open' : ''}`} />
      </button>
      {open && (
        <div className="tp-tiles">
          {areas.map((a) => (
            <ClickCard key={a.key} className={`tp-tile tp-tile--${tone}`} onClick={() => onArea(a)} label={`Show the work in ${a.name}`}>
              <Icon size={14} /> <span className="tp-tile__name">{a.name}</span>
            </ClickCard>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TeamProgress() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const attentionRef = useRef(null);

  const fetchProgress = useCallback(async () => {
    try {
      const res = await api.get('/internal/team-progress');
      return { next: res.data, error: null };
    } catch (err) {
      if (err?.response?.status === 404) return { next: null, error: 'This page is not available on the server yet.' };
      return { next: null, error: err?.response?.data?.detail || 'Could not load team progress.' };
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchProgress().then((result) => {
      if (cancelled) return;
      setData(result.next);
      setError(result.error);
      setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [fetchProgress]);

  const load = async () => {
    setIsLoading(true);
    const result = await fetchProgress();
    setData(result.next);
    setError(result.error);
    setIsLoading(false);
  };

  const closeDetail = useCallback(() => setDetail(null), []);

  const s = data?.summary;
  const items = useMemo(() => data?.items || [], [data]);
  const itemsByKey = useMemo(() => Object.fromEntries(items.map((i) => [i.key, i])), [items]);
  const areas = data?.areas || [];
  const inProgress = areas.filter((a) => a.pct < 100 && !a.parked);
  const parked = areas.filter((a) => a.parked && a.pct < 100);
  const deliveredAreas = areas.filter((a) => a.pct === 100);
  const weekly = data?.weekly || [];
  const weekMax = Math.max(1, ...weekly.map((w) => w.finished));
  const updated = data?.generated_at
    ? new Date(data.generated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    : null;

  // Same windows the server counts with: "this week" is the last 7 days.
  const weekAgo = isoDaysAgo(7);
  const deliveredThisWeek = sortDone(items.filter((i) => i.state === 'done' && i.done_on && i.done_on > weekAgo));
  const sprints = s?.sprints || [];
  const sprintRunning = sprints.length > 0;
  const people = useMemo(
    () => (sprintRunning ? groupByPerson(items.filter((i) => i.in_sprint), true) : groupByPerson(items, false)),
    [items, sprintRunning],
  );
  const sprintItems = (sp) => items.filter((i) => (i.sprints || []).includes(sp.name));

  // Coming next: what's built and waiting for the next release (In Review),
  // then what's still being built: In Progress, and To Do with a due date.
  const waitingForRelease = items.filter((i) => i.state === 'testing').sort(byDue);
  const nextUp = items.filter((i) => i.state === 'doing' || (i.state === 'todo' && i.due)).sort(byDue);

  // This week by area, busiest first: three chips and "+ N areas".
  const deliveredByArea = Object.entries(
    deliveredThisWeek.reduce((acc, i) => ({ ...acc, [i.area || 'Other']: (acc[i.area || 'Other'] || 0) + 1 }), {}),
  ).sort((a, b) => b[1] - a[1]);
  const pastWeeks = weekly.slice(0, -1);
  const weekAvg = pastWeeks.length ? Math.round(pastWeeks.reduce((t, w) => t + w.finished, 0) / pastWeeks.length) : 0;
  const sprintEnds = [...new Set(sprints.map((sp) => sp.ends).filter(Boolean))];

  const open = {
    sprint: (sp) => {
      const list = sprintItems(sp);
      const waiting = list.filter((i) => i.state === 'testing').length;
      setDetail({
        title: sprintTitle(sp),
        subtitle: `${readyPct(list)}% ready · ${waiting} waiting for the next release${sp.ends ? ` · ends ${shortDate(sp.ends)}` : ''}`,
        ...splitTabs(list),
      });
    },
    all: () => setDetail({
      title: 'All tracked work',
      subtitle: `${s.overall_pct ?? 0}% done`,
      ...splitTabs(items),
    }),
    delivered: () => setDetail({
      title: 'Delivered this week',
      subtitle: `${plural(deliveredThisWeek.length, 'piece', 'pieces')} of work in the last 7 days`,
      tabs: [{ label: 'Delivered', kind: 'done', items: deliveredThisWeek }],
    }),
    featuresFixes: () => setDetail({
      title: 'New features vs fixes',
      subtitle: 'Delivered in the last 7 days',
      tabs: [
        { label: 'New features', kind: 'done', items: deliveredThisWeek.filter((i) => i.kind === 'feature') },
        { label: 'Fixes', kind: 'done', items: deliveredThisWeek.filter((i) => i.kind === 'fix') },
      ],
    }),
    item: (key) => {
      const it = itemsByKey[key];
      if (!it) return;
      setDetail({ title: it.area || 'Work item', subtitle: lineNote(it).text || STATE_LABEL[it.state], single: it });
    },
    area: (a) => setDetail({
      title: a.name,
      subtitle: `${a.pct}% done · ${a.done} of ${a.total} pieces of work`,
      ...splitTabs(items.filter((i) => i.area_key === a.key)),
    }),
    // The row counts this release; the pop-up lists all of the person's work,
    // older finished work included.
    person: (p) => setDetail({
      title: p.name || 'No owner',
      subtitle: p.name
        ? `${plural(p.open, 'open piece', 'open pieces')} of work${sprintRunning ? ` · ${p.counts.done} done this release` : ''}`
        : 'Nobody is assigned to these yet',
      ...splitTabs(items.filter((i) => (i.owner || null) === p.name)),
    }),
    week: (w) => {
      const start = w.week_start;
      const endDate = new Date(`${start}T00:00:00`);
      endDate.setDate(endDate.getDate() + 7);
      const end = endDate.toLocaleDateString('en-CA');
      setDetail({
        title: `Week of ${shortDate(start)}`,
        subtitle: `${plural(w.finished, 'piece', 'pieces')} of work delivered`,
        tabs: [{ label: 'Delivered', kind: 'done', items: items.filter((i) => i.state === 'done' && i.done_on >= start && i.done_on < end) }],
      });
    },
  };

  const scrollToAttention = () => attentionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="tp-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Team progress</h1>
          <p className="page-subtitle">How the product work is going{updated ? ` · updated ${updated}` : ''} · click any card for details</p>
        </div>
        <button className="btn btn--secondary" onClick={load} disabled={isLoading}>
          <RefreshCw size={16} className={isLoading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {error && <div className="tp-error"><AlertTriangle size={16} /> {error}</div>}

      {isLoading && !data ? (
        <div className="tp-loading"><Loader2 size={28} className="spin" /></div>
      ) : data && data.configured === false ? (
        <div className="card tp-empty">
          <Clock size={20} />
          <span>Team progress is not set up on this server yet. It needs a Jira API key (JIRA_BASE_URL, JIRA_EMAIL and JIRA_API_TOKEN in the backend settings).</span>
        </div>
      ) : !data ? null : (
        <div className="tp-stack">
          <div className="card tp-hero">
            <p className="tp-hero__label">
              {sprints.length > 0
                ? `This release${sprintEnds.length === 1 ? ` · ends ${shortDate(sprintEnds[0])}` : ''}`
                : 'No sprint is running right now'}
            </p>
            <div className="tp-rings">
              {sprintRunning ? sprints.map((sp) => (
                <SprintCard
                  key={sp.name}
                  title={sp.label}
                  sub={[sp.version, sprintEnds.length > 1 && sp.ends ? `ends ${shortDate(sp.ends)}` : null].filter(Boolean).join(' · ')}
                  list={sprintItems(sp)}
                  onClick={() => open.sprint(sp)}
                  label={`Show the work in ${sprintTitle(sp)}`}
                />
              )) : (
                <SprintCard title="All tracked work" list={items} onClick={open.all} label="Show all tracked work" />
              )}
            </div>
            <div className="tp-pills">
              {s.late > 0 && <button type="button" className="tp-pill-btn" onClick={scrollToAttention}><Pill tone="bad">{s.late} late</Pill></button>}
              {s.stuck > 0 && <button type="button" className="tp-pill-btn" onClick={scrollToAttention}><Pill tone="warn">{s.stuck} stalled</Pill></button>}
              {s.late === 0 && s.stuck === 0 && <Pill tone="good">Nothing late or stalled</Pill>}
            </div>
          </div>

          <section ref={attentionRef} className="tp-anchor">
            <h3 className="tp-heading">Needs attention</h3>
            {data.at_risk.length === 0 ? (
              <div className="tp-calm"><CheckCircle2 size={18} /> Nothing is late or stalled.</div>
            ) : (
              <div className="tp-grid">
                {data.at_risk.map((r) => (
                  <ClickCard
                    key={r.key}
                    className={`tp-alert tp-alert--${r.reason === 'late' ? 'bad' : 'warn'}`}
                    onClick={() => open.item(r.key)}
                    label={`Details for ${r.summary}`}
                  >
                    <span className="tp-alert__icon">{r.reason === 'late' ? <AlertTriangle size={16} /> : <PauseCircle size={16} />}</span>
                    <span className="tp-alert__body">
                      <span className="tp-alert__title">{r.summary}</span>
                      <span className="tp-alert__sub">
                        {r.reason === 'late' ? `Late since ${shortDate(r.due)}` : `No progress for ${r.days} days`}
                        {r.area ? ` · ${r.area}` : ''}
                      </span>
                    </span>
                  </ClickCard>
                ))}
              </div>
            )}
          </section>

          <section>
            <h3 className="tp-heading">Coming next</h3>
            <div className="card tp-coming">
              {waitingForRelease.length === 0 && nextUp.length === 0 ? (
                <div className="tp-calm tp-calm--plain"><Clock size={18} /> Nothing is waiting for release or due yet.</div>
              ) : (
                <>
                  {waitingForRelease.length > 0 && (
                    <ComingGroup
                      release
                      title={`Waiting for the next release · ${waitingForRelease.length}`}
                      list={waitingForRelease}
                      onItem={open.item}
                      onAll={() => setDetail({ title: 'Waiting for the next release', subtitle: 'Built and on preprod (In Review)', tabs: [{ label: 'Waiting', kind: 'open', items: waitingForRelease }] })}
                    />
                  )}
                  {nextUp.length > 0 && (
                    <ComingGroup
                      showStatus
                      title={`Next up · ${nextUp.length}`}
                      list={nextUp}
                      onItem={open.item}
                      onAll={() => setDetail({ title: 'Next up', subtitle: 'Still being built, soonest due first', tabs: [{ label: 'Next up', kind: 'open', items: nextUp }] })}
                    />
                  )}
                  <p className="tp-modal__hint">Click a line for the full title, owner and ticket.</p>
                </>
              )}
            </div>
          </section>

          {people.length > 0 && (
            <section>
              <h3 className="tp-heading">
                Who&apos;s working on what <span className="tp-heading__count">· {sprintRunning ? 'this release' : 'open work'}</span>
              </h3>
              <div className="card tp-people">
                <ul className="tp-people__list">
                  {people.map((p) => <PersonRow key={p.name ?? '-'} person={p} maxTotal={Math.max(...people.map((x) => x.total))} onClick={() => open.person(p)} />)}
                </ul>
                <div className="tp-people__legend">
                  {PERSON_PARTS.filter((p) => sprintRunning || p.cls !== 'done').map((p) => <span key={p.cls}><i className={`tp-dot tp-dot--${p.cls}`} />{p.label}</span>)}
                  <span className="tp-people__hint">Click a person to see their list</span>
                </div>
              </div>
            </section>
          )}

          {/* What was shipped this week, in one card: the count, what kind,
              where, the newest few, and the weekly trend beside it. */}
          <section>
            <h3 className="tp-heading">This week</h3>
            <div className="card tp-weekcard">
              <div className="tp-weekcard__main">
                <div className="tp-weekcard__top">
                  <button type="button" className="tp-weekcard__count" onClick={open.delivered}>
                    <span className="tp-weekcard__num">{s.finished_this_week}</span> delivered
                  </button>
                  <WeekCompare now={s.finished_this_week} before={s.finished_last_week} />
                </div>
                <button type="button" className="tp-link tp-link--inline tp-weekcard__kinds" onClick={open.featuresFixes}>
                  {plural(s.features_this_week, 'feature', 'features')} · {plural(s.fixes_this_week, 'fix', 'fixes')}
                </button>
                {deliveredByArea.length > 0 && (
                  <div className="tp-chips">
                    {deliveredByArea.slice(0, 3).map(([area, n]) => <span key={area} className="tp-chip">{area} · {n}</span>)}
                    {deliveredByArea.length > 3 && <span className="tp-chip tp-chip--muted">+ {plural(deliveredByArea.length - 3, 'area', 'areas')}</span>}
                  </div>
                )}
                {deliveredThisWeek.length === 0 ? (
                  <div className="tp-calm tp-calm--plain"><Clock size={18} /> Nothing delivered in the last 7 days.</div>
                ) : (
                  <ul className="tp-newest">
                    {deliveredThisWeek.slice(0, 4).map((d) => (
                      <li key={d.key}>
                        <button type="button" className="tp-cline" onClick={() => open.item(d.key)}>
                          <Check size={14} className="tp-ticks__icon" />
                          {d.kind === 'fix' && <span className="tp-fix">Fix</span>}
                          <span className="tp-cline__title">{d.summary}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {deliveredThisWeek.length > 4 && (
                  <button type="button" className="tp-link tp-more" onClick={open.delivered}>See all {deliveredThisWeek.length} <ChevronRight size={14} /></button>
                )}
              </div>

              <div className="tp-trend">
                <div className="tp-trend__label">
                  <span>Delivered per week</span>
                  <span className="tp-trend__sub">this week in orange{weekAvg > 0 ? ` · dashed line = average (${weekAvg})` : ''}</span>
                </div>
                <div className="tp-trend__plot">
                  {weekAvg > 0 && <span className="tp-trend__avg" style={{ bottom: `${Math.round((64 * weekAvg) / weekMax)}px` }} />}
                  {weekly.map((w, i) => (
                    <button
                      type="button"
                      key={w.week_start}
                      className="tp-trend__col"
                      onClick={() => open.week(w)}
                      aria-label={`Week of ${shortDate(w.week_start)}: ${w.finished} delivered`}
                    >
                      <span className="tp-trend__value">{w.finished}</span>
                      <span
                        className={`tp-trend__bar${i === weekly.length - 1 ? ' tp-trend__bar--now' : ''}`}
                        style={{ height: `${Math.max(3, Math.round((64 * w.finished) / weekMax))}px` }}
                      />
                    </button>
                  ))}
                </div>
                <div className="tp-trend__weeks">
                  {weekly.map((w, i) => <span key={w.week_start}>{i === weekly.length - 1 ? 'This week' : shortDate(w.week_start)}</span>)}
                </div>
              </div>
            </div>
          </section>

          {/* Areas last: reference. Finished and parked areas fold to one line each. */}
          <section>
            <h3 className="tp-heading">
              Areas <span className="tp-heading__count">· {s.overall_pct}% of all work done · {s.areas_delivered} of {s.areas_total} delivered</span>
            </h3>
            {inProgress.length === 0 ? (
              <div className="tp-calm"><CheckCircle2 size={18} /> Every area is delivered.</div>
            ) : (
              <div className="tp-grid">
                {inProgress.map((a) => {
                  const st = areaStatus(a);
                  return (
                    <ClickCard key={a.key} className="card tp-area" onClick={() => open.area(a)} label={`Show the work in ${a.name}`}>
                      <Ring pct={a.pct} size={64} stroke={7} color={EPIC_COLORS[a.color] || 'var(--accent-primary)'} label={14} />
                      <span className="tp-area__body">
                        <span className="tp-area__name">{a.name}</span>
                        <span className="tp-area__count">{a.done} of {a.total} done</span>
                        <Pill tone={st.tone}>{st.text}</Pill>
                      </span>
                    </ClickCard>
                  );
                })}
              </div>
            )}
            {(deliveredAreas.length > 0 || parked.length > 0) && (
              <div className="tp-folds">
                {deliveredAreas.length > 0 && (
                  <FoldTiles icon={Check} tone="good" label={plural(deliveredAreas.length, 'area delivered', 'areas delivered')} areas={deliveredAreas} onArea={open.area} />
                )}
                {parked.length > 0 && (
                  <FoldTiles icon={PauseCircle} tone="muted" label={`${parked.length} parked for later`} areas={parked} onArea={open.area} />
                )}
              </div>
            )}
          </section>
        </div>
      )}

      {detail && <DetailModal detail={detail} jiraUrl={data?.jira_url} onClose={closeDetail} />}

      <style>{`
        .tp-page { max-width: 1100px; }
        .tp-page .page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: var(--space-6); gap: var(--space-4); flex-wrap: wrap; }
        .tp-page .page-title { font-size: var(--text-2xl); font-weight: 600; margin-bottom: var(--space-2); letter-spacing: -0.02em; }
        .tp-page .page-subtitle { font-size: var(--text-sm); color: var(--text-tertiary); }
        .tp-stack { display: flex; flex-direction: column; gap: var(--space-6); }

        .tp-error { display: flex; align-items: center; gap: var(--space-2); padding: var(--space-3) var(--space-4); margin-bottom: var(--space-6); background: var(--status-danger-subtle); color: var(--status-danger); border-radius: var(--radius-md); font-size: var(--text-sm); }
        .tp-loading { display: flex; justify-content: center; padding: var(--space-10) 0; color: var(--text-tertiary); }
        .card.tp-empty { display: flex; align-items: center; gap: var(--space-3); color: var(--text-tertiary); font-size: var(--text-sm); }

        .tp-click { font: inherit; color: inherit; text-align: left; cursor: pointer; transition: border-color var(--duration-fast) var(--ease-out), transform var(--duration-fast) var(--ease-out); }
        .tp-click:hover { border-color: var(--border-hover); transform: translateY(-1px); }
        .tp-click:focus-visible, .tp-link:focus-visible, .tp-pill-btn:focus-visible, .tp-cline:focus-visible, .tp-trend__col:focus-visible, .tp-fold__toggle:focus-visible, .tp-weekcard__count:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) { .tp-click, .tp-click:hover { transition: none; transform: none; } }

        .tp-ring__label { fill: var(--text-primary); font-weight: 600; font-variant-numeric: tabular-nums; }

        .tp-hero { display: flex; flex-direction: column; gap: var(--space-3); }
        .tp-hero__label { margin: 0; font-size: var(--text-sm); color: var(--text-secondary); }
        .tp-rings { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: var(--space-3); }
        .tp-sprint { display: flex; flex-direction: column; gap: var(--space-2); width: 100%; padding: var(--space-3) var(--space-4); background: var(--bg-surface); border: 1px solid var(--border-default); border-radius: var(--radius-lg); }
        .tp-sprint__head { display: flex; align-items: baseline; justify-content: space-between; gap: var(--space-3); }
        .tp-sprint__pct { font-size: var(--text-xl); font-weight: 600; letter-spacing: -0.02em; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .tp-sprint__pct small { font-size: var(--text-xs); font-weight: 400; color: var(--text-secondary); }
        .tp-sprint .tp-bar2 { gap: 6px; }
        .tp-sprint .tp-bar2__track { height: 10px; }
        .tp-bar2__extra { margin-left: auto; color: var(--text-tertiary); }
        .tp-sprint__waiting { color: var(--status-info); font-weight: 500; }
        .tp-sprint__title { font-size: var(--text-lg); font-weight: 600; letter-spacing: -0.01em; }
        .tp-sprint__sub { font-size: var(--text-sm); font-weight: 400; color: var(--text-secondary); letter-spacing: 0; }
        .tp-pills { display: flex; flex-wrap: wrap; gap: var(--space-2); }
        .tp-pill-btn { background: none; border: none; padding: 0; cursor: pointer; border-radius: 999px; }
        .tp-pill-btn:hover .tp-pill { filter: brightness(1.15); }

        .tp-pill { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 999px; font-size: var(--text-xs); font-weight: 500; white-space: nowrap; width: fit-content; }
        .tp-pill--good { background: var(--status-success-subtle); color: var(--status-success); }
        .tp-pill--warn { background: var(--status-warning-subtle); color: var(--status-warning); }
        .tp-pill--bad { background: var(--status-danger-subtle); color: var(--status-danger); }
        .tp-pill--none { background: var(--bg-subtle); color: var(--text-tertiary); }

        .tp-compare { display: inline-flex; align-items: center; gap: 2px; font-size: var(--text-xs); font-weight: 500; }
        .tp-compare--good { color: var(--status-success); }
        .tp-compare--bad { color: var(--status-danger); }
        .tp-compare--none { color: var(--text-tertiary); }

        .tp-anchor { scroll-margin-top: var(--space-8); }
        .tp-heading { font-size: var(--text-base); font-weight: 600; margin: 0 0 var(--space-3); }
        .tp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: var(--space-3); }
        .tp-calm { display: flex; align-items: center; gap: var(--space-2); padding: var(--space-3) var(--space-4); border-radius: var(--radius-md); background: var(--status-success-subtle); color: var(--status-success); font-size: var(--text-sm); }
        .tp-calm--plain { background: none; padding: 0; color: var(--text-tertiary); }

        .tp-alert { display: flex; gap: var(--space-3); padding: var(--space-3) var(--space-4); border-radius: var(--radius-md); border: 1px solid; }
        .tp-alert--bad { background: var(--status-danger-subtle); border-color: rgba(239, 68, 68, 0.35); }
        .tp-alert--warn { background: var(--status-warning-subtle); border-color: rgba(245, 158, 11, 0.35); }
        .tp-alert--bad .tp-alert__icon { color: var(--status-danger); }
        .tp-alert--warn .tp-alert__icon { color: var(--status-warning); }
        .tp-alert__icon { padding-top: 2px; flex-shrink: 0; }
        .tp-alert__body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .tp-alert__title { font-size: var(--text-sm); font-weight: 500; color: var(--text-primary); }
        .tp-alert__sub { font-size: var(--text-xs); color: var(--text-secondary); }

        .card.tp-area { display: flex; align-items: center; gap: var(--space-4); padding: var(--space-4); width: 100%; }
        .tp-area__body { display: flex; flex-direction: column; gap: var(--space-1); min-width: 0; }
        .tp-area__name { font-size: var(--text-base); font-weight: 600; }
        .tp-area__count { font-size: var(--text-xs); color: var(--text-tertiary); font-variant-numeric: tabular-nums; }

        .tp-heading { display: flex; align-items: center; gap: 6px; }
        .tp-heading__count { font-weight: 400; color: var(--text-tertiary); font-variant-numeric: tabular-nums; }
        .tp-tiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: var(--space-2); }
        .tp-tile { display: flex; align-items: center; gap: 6px; padding: 8px 12px; border-radius: var(--radius-md); border: 1px solid; font-size: var(--text-sm); }
        .tp-tile__name { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .tp-tile--good { background: var(--status-success-subtle); border-color: rgba(34, 197, 94, 0.3); color: var(--status-success); }
        .tp-tile--good .tp-tile__name { color: var(--text-primary); }
        .tp-tile--muted { background: var(--bg-surface); border-color: var(--border-default); color: var(--text-tertiary); }
        .tp-tile--muted .tp-tile__name { color: var(--text-secondary); }
        .tp-link { display: inline-flex; align-items: center; gap: 2px; background: none; border: none; padding: 0 0 0 var(--space-1); color: var(--accent-primary); font: inherit; cursor: pointer; border-radius: 2px; }
        .tp-link:hover { text-decoration: underline; }
        .tp-link--inline { padding: 0; color: var(--text-secondary); text-decoration: underline dotted; text-underline-offset: 3px; }
        .tp-link--inline:hover { color: var(--text-primary); }

        .card.tp-people { display: flex; flex-direction: column; gap: var(--space-3); padding: var(--space-3) var(--space-4); }
        .tp-people__list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
        .tp-people__list li + li { border-top: 1px solid var(--divider); }
        .tp-person { display: grid; grid-template-columns: 32px minmax(90px, 160px) minmax(80px, 180px) 1fr 16px; align-items: center; gap: var(--space-3); width: 100%; padding: 10px 6px; background: none; border: none; font: inherit; color: inherit; text-align: left; cursor: pointer; border-radius: var(--radius-md); }
        .tp-person:hover { background: rgba(127, 127, 127, 0.06); }
        .tp-person:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: -2px; }
        .tp-person__avatar { width: 32px; height: 32px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; background: var(--bg-subtle); color: var(--text-secondary); font-size: var(--text-xs); font-weight: 600; }
        .tp-person__name { font-size: var(--text-sm); font-weight: 600; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .tp-person__bar { display: flex; gap: 2px; height: 8px; border-radius: 999px; overflow: hidden; }
        .tp-person__text { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; font-size: var(--text-sm); color: var(--text-secondary); min-width: 0; font-variant-numeric: tabular-nums; }
        .tp-person__go { color: var(--text-tertiary); }
        .tp-person--nobody { background: var(--status-warning-subtle); }
        .tp-person--nobody:hover { background: var(--status-warning-subtle); filter: brightness(1.1); }
        .tp-person--nobody .tp-person__avatar { background: transparent; color: var(--status-warning); }
        .tp-person--nobody .tp-person__name, .tp-person--nobody .tp-person__text { color: var(--status-warning); }
        .tp-people__legend { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-2) var(--space-4); font-size: var(--text-xs); color: var(--text-tertiary); }
        .tp-people__legend span { display: inline-flex; align-items: center; gap: 6px; }
        .tp-people__hint { margin-left: auto; }

        .tp-ticks__icon { color: var(--status-success); flex-shrink: 0; }
        .tp-more { margin-top: var(--space-2); padding-left: 0; font-size: var(--text-xs); }

        .card.tp-coming { display: flex; flex-direction: column; gap: var(--space-4); }
        .tp-cgroup { display: flex; flex-direction: column; }
        .tp-cgroup__title { display: flex; align-items: center; gap: 6px; margin: 0 0 var(--space-1); font-size: var(--text-xs); font-weight: 600; color: var(--text-secondary); }
        .tp-cgroup__title--release { color: var(--status-info); }
        .tp-clines, .tp-newest { list-style: none; margin: 0; padding: 0; }
        .tp-clines li, .tp-newest li { border-bottom: 1px solid var(--divider); }
        .tp-clines li:last-child, .tp-newest li:last-child { border-bottom: none; }
        .tp-cline { display: flex; align-items: center; gap: var(--space-2); width: 100%; padding: 8px 0; background: none; border: none; font: inherit; font-size: var(--text-sm); color: inherit; text-align: left; cursor: pointer; border-radius: 2px; }
        .tp-cline:hover .tp-cline__title { color: var(--accent-primary); }
        .tp-cline__title { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .tp-cline__area { flex-shrink: 0; font-size: var(--text-xs); color: var(--text-tertiary); white-space: nowrap; }
        .tp-cline__due { flex-shrink: 0; min-width: 76px; text-align: right; font-size: var(--text-xs); color: var(--text-secondary); white-space: nowrap; font-variant-numeric: tabular-nums; }
        .tp-cline__due--late { color: var(--status-danger); font-weight: 600; }
        .tp-fix { flex-shrink: 0; padding: 0 6px; border-radius: var(--radius-sm); font-size: 11px; font-weight: 600; background: var(--status-warning-subtle); color: var(--status-warning); }

        .card.tp-weekcard { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: var(--space-6); align-items: start; }
        .tp-weekcard__main { display: flex; flex-direction: column; gap: var(--space-2); min-width: 0; }
        .tp-weekcard__top { display: flex; align-items: baseline; gap: var(--space-3); flex-wrap: wrap; }
        .tp-weekcard__count { display: inline-flex; align-items: baseline; gap: 6px; background: none; border: none; padding: 0; font: inherit; font-size: var(--text-sm); color: var(--text-secondary); cursor: pointer; border-radius: 2px; }
        .tp-weekcard__num { font-size: var(--text-2xl); font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); font-variant-numeric: tabular-nums; }
        .tp-weekcard__kinds { align-self: flex-start; font-size: var(--text-sm); }
        .tp-chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .tp-chip { padding: 2px 8px; border-radius: var(--radius-sm); font-size: var(--text-xs); background: var(--status-success-subtle); color: var(--status-success); white-space: nowrap; }
        .tp-chip--muted { background: var(--bg-subtle); color: var(--text-tertiary); }
        .tp-newest { margin-top: var(--space-1); }

        .tp-folds { display: flex; flex-direction: column; gap: var(--space-2); margin-top: var(--space-3); }
        .tp-fold { display: flex; flex-direction: column; gap: var(--space-2); }
        .tp-fold__toggle { align-self: flex-start; display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: var(--radius-md); border: 1px solid; font: inherit; font-size: var(--text-sm); cursor: pointer; }
        .tp-fold__toggle--good { background: var(--status-success-subtle); border-color: rgba(34, 197, 94, 0.3); color: var(--status-success); }
        .tp-fold__toggle--muted { background: var(--bg-surface); border-color: var(--border-default); color: var(--text-secondary); }
        .tp-fold__chev { transition: transform var(--duration-fast) var(--ease-out); }
        .tp-fold__chev--open { transform: rotate(90deg); }

        .tp-trend { display: flex; flex-direction: column; gap: var(--space-2); min-width: 0; }
        .tp-trend__label { display: flex; flex-direction: column; gap: 2px; font-size: var(--text-sm); color: var(--text-secondary); }
        .tp-trend__sub { font-size: var(--text-xs); color: var(--text-tertiary); }
        .tp-trend__plot { position: relative; display: flex; align-items: flex-end; gap: var(--space-2); height: 86px; }
        .tp-trend__avg { position: absolute; left: 0; right: 0; border-top: 1px dashed var(--text-tertiary); opacity: 0.6; pointer-events: none; }
        .tp-trend__col { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; gap: 3px; height: 100%; background: none; border: none; padding: 0; font: inherit; cursor: pointer; border-radius: 4px; }
        .tp-trend__col:hover .tp-trend__bar { filter: brightness(1.2); }
        .tp-trend__value { font-size: var(--text-xs); color: var(--text-secondary); font-variant-numeric: tabular-nums; }
        .tp-trend__bar { display: block; width: 100%; max-width: 36px; border-radius: 4px 4px 0 0; background: var(--text-quaternary); }
        .tp-trend__bar--now { background: #FE7B00; }
        .tp-trend__weeks { display: flex; gap: var(--space-2); }
        .tp-trend__weeks span { flex: 1; text-align: center; font-size: 10px; color: var(--text-tertiary); white-space: nowrap; overflow: hidden; }

        .modal.tp-modal { width: min(600px, 92vw); max-height: 85vh; display: flex; flex-direction: column; }
        .tp-modal__head { min-width: 0; }
        .tp-modal__sub { margin: var(--space-1) 0 0; font-size: var(--text-sm); color: var(--text-secondary); }
        .tp-modal__body { overflow-y: auto; display: flex; flex-direction: column; gap: var(--space-5); }
        .tp-modal__hint { margin: 0; font-size: var(--text-xs); color: var(--text-tertiary); }

        .tp-bar2 { display: flex; flex-direction: column; gap: var(--space-2); }
        .tp-bar2__track { display: flex; gap: 3px; height: 10px; border-radius: 999px; overflow: hidden; }
        .tp-bar2__seg--done { background: var(--status-success); }
        .tp-bar2__seg--doing { background: var(--status-warning); }
        .tp-bar2__seg--todo { background: var(--text-quaternary); }
        .tp-bar2__seg--testing { background: var(--status-info); }
        .tp-bar2__legend { display: flex; flex-wrap: wrap; gap: var(--space-4); font-size: var(--text-xs); color: var(--text-secondary); font-variant-numeric: tabular-nums; }
        .tp-bar2__legend span { display: inline-flex; align-items: center; gap: 6px; }
        .tp-dot { width: 8px; height: 8px; border-radius: 2px; display: inline-block; }
        .tp-dot--done { background: var(--status-success); }
        .tp-dot--doing { background: var(--status-warning); }
        .tp-dot--todo { background: var(--text-quaternary); }
        .tp-dot--testing { background: var(--status-info); }

        .tp-tabs { display: flex; gap: var(--space-2); flex-wrap: wrap; }
        .tp-tab { padding: 6px 14px; border-radius: 999px; border: 1px solid var(--border-default); background: transparent; color: var(--text-secondary); font: inherit; font-size: var(--text-sm); cursor: pointer; font-variant-numeric: tabular-nums; }
        .tp-tab:hover { border-color: var(--border-hover); color: var(--text-primary); }
        .tp-tab--on { background: var(--text-primary); border-color: var(--text-primary); color: var(--bg-base); font-weight: 500; }
        .tp-tab:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: 2px; }

        .tp-popsec { display: flex; flex-direction: column; gap: var(--space-2); }
        .tp-popsec + .tp-popsec { margin-top: var(--space-4); }
        .tp-popsec__title { display: flex; align-items: center; gap: 6px; margin: 0; font-size: var(--text-sm); font-weight: 600; color: var(--text-secondary); }
        .tp-popsec__title--bad { color: var(--status-danger); }

        .tp-lines { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
        .tp-line { border-radius: var(--radius-md); border: 1px solid var(--border-default); background: var(--bg-surface); }
        .tp-line--bad { border-color: rgba(239, 68, 68, 0.4); background: var(--status-danger-subtle); }
        .tp-line--warn { border-color: rgba(245, 158, 11, 0.4); background: var(--status-warning-subtle); }
        .tp-line__row { display: flex; align-items: center; justify-content: space-between; gap: var(--space-4); width: 100%; padding: 10px 14px; background: none; border: none; font: inherit; color: inherit; text-align: left; cursor: pointer; border-radius: inherit; }
        .tp-line__row:hover { background: rgba(127, 127, 127, 0.06); }
        .tp-line__row:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: -2px; }
        .tp-line__title { min-width: 0; font-size: var(--text-sm); color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .tp-line--open .tp-line__title { white-space: normal; font-weight: 500; line-height: 1.45; }
        .tp-status { display: inline-flex; align-items: center; padding: 1px 8px; border-radius: var(--radius-sm); font-size: var(--text-xs); font-weight: 600; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .tp-status--todo { background: var(--bg-subtle); color: var(--text-secondary); }
        .tp-status--doing { background: var(--status-warning-subtle); color: var(--status-warning); }
        .tp-status--testing { background: var(--status-info-subtle); color: var(--status-info); }
        .tp-status--done { background: var(--status-success-subtle); color: var(--status-success); }
        .tp-line__side { display: inline-flex; align-items: center; gap: var(--space-2); flex-shrink: 0; }
        .tp-line__when { flex-shrink: 0; font-size: var(--text-xs); color: var(--text-secondary); white-space: nowrap; font-variant-numeric: tabular-nums; }
        .tp-line--bad .tp-line__when { color: var(--status-danger); font-weight: 600; }
        .tp-line--warn .tp-line__when { color: var(--status-warning); font-weight: 600; }
        .tp-line__detail { padding: 0 14px 12px; }
        .tp-line__facts { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-2) var(--space-3); font-size: var(--text-xs); color: var(--text-secondary); }
        .tp-kind { display: inline-flex; padding: 1px 8px; border-radius: var(--radius-sm); font-weight: 500; }
        .tp-kind--feature { background: var(--accent-primary-subtle); color: var(--accent-primary); }
        .tp-kind--fix { background: var(--status-warning-subtle); color: var(--status-warning); }
        .tp-jira { display: inline-flex; align-items: center; gap: 4px; color: var(--accent-primary); text-decoration: none; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .tp-jira:hover { text-decoration: underline; }
        .tp-showmore { align-self: flex-start; padding-left: 0; font-size: var(--text-sm); }

        @media (max-width: 900px) {
          .tp-hero { gap: var(--space-3); }
          .card.tp-weekcard { grid-template-columns: 1fr; }
          .tp-cline__area { display: none; }
          .tp-person { grid-template-columns: 32px 1fr 16px; row-gap: 6px; }
          .tp-person__bar, .tp-person__text { grid-column: 2; }
          .tp-person__go { grid-row: 1; grid-column: 3; }
          .tp-people__hint { margin-left: 0; }
        }
      `}</style>
    </div>
  );
}
